const { initCarouselElement } = require('./components/swiperCarousel').default;
const { queryFirst, queryAll, toggleClass, hasClass, setAttribute } = require('./domUtil');
const { HIDDEN_CLASS, SHOW_CLASS, GWP_OPEN_JOIN_PARAM } = require('./constants');

const PANEL_SELECTOR = '#sticky-gwp-drawer-panel';
const TOGGLE_SELECTOR = '[data-sticky-gwp-toggle]';
const EXPANDED_CLASS = 'sticky-gwp-drawer-expanded';

/**
 * Init or refresh the card carousel once the panel is visible (has real dimensions).
 * @param {HTMLElement} panel - the modal panel element.
 */
function initPanelCarousel(panel) {
    const swiperEl = queryFirst('.js-swiper', panel);
    if (!swiperEl) return;
    if (swiperEl.carousel) {
        swiperEl.carousel.update();
    } else {
        initCarouselElement(swiperEl);
    }
}

/**
 * Sync bar (chevrons + aria) and drawer with open/closed state. When expanded,
 * the .expanded class drops the sticky-CTA offset so the panel sits at
 * the bottom; collapsed, the bar stays above the CTA.
 * @param {HTMLElement} drawer - the drawer root element.
 * @param {boolean} expanded - true when the panel is open.
 */
function syncToggleState(drawer, expanded) {
    const toggle = queryFirst(TOGGLE_SELECTOR, drawer);
    toggleClass(drawer, EXPANDED_CLASS, expanded);
    if (toggle) setAttribute(toggle, 'aria-expanded', String(expanded));
    toggleClass(queryFirst('.sticky-gwp-drawer-chevron-up', drawer), HIDDEN_CLASS, expanded);
    toggleClass(queryFirst('.sticky-gwp-drawer-chevron-down', drawer), HIDDEN_CLASS, !expanded);
}

/**
 * Fetch and open the GWP promo details sheet for the given sheet ID.
 * @param {string} promoSheetId - the promo sheet ID (PD page or content asset).
 * @param {string} promoPDSheetAvailable - '"true"' when the ID is a PD page.
 */
function openGwpPromoSheet(promoSheetId, promoPDSheetAvailable) {
    const container = queryFirst('.sticky-gwp-promo-sheet-container');
    if (!container) return;
    const { promoDetailsUrl } = container.dataset;
    if (!promoDetailsUrl || !promoSheetId) return;

    let holder = queryFirst('#sticky-gwp-promo-container-' + promoSheetId);
    if (holder && holder.innerHTML.trim()) {
        const modal = queryFirst('#promo-' + promoSheetId);
        if (modal) $(modal).modal('show');
        return;
    }

    const url = new URL(promoDetailsUrl, window.location.origin);
    url.searchParams.set('promoSheetId', promoSheetId);
    url.searchParams.set('promoPDSheetAvailable', promoPDSheetAvailable);

    fetch(url.toString())
        .then(function (res) { return res.text(); })
        .then(function (html) {
            if (!html) return;
            if (!holder) {
                holder = document.createElement('div');
                holder.id = 'sticky-gwp-promo-container-' + promoSheetId;
                document.body.appendChild(holder);
            }
            holder.innerHTML = html;
            const modal = queryFirst('#promo-' + promoSheetId);
            if (modal) $(modal).modal('show');
        })
        .catch(function () {
            if (holder) holder.innerHTML = '';
        });
}

/**
 * Toggle the drawer and sync the bar. Backdrop, scroll lock, focus trap,
 * and Escape are handled by components/modal.js.
 * @param {HTMLElement} drawer - the drawer root element.
 */
function initDrawer(drawer) {
    if (!drawer || drawer.dataset.stickyGwpInitialized === 'true') return;
    drawer.dataset.stickyGwpInitialized = 'true';

    const panel = queryFirst(PANEL_SELECTOR, drawer);
    if (!panel) return;
    const $panel = $(panel);

    queryAll('.swiper-slide', panel).forEach(function (slide) {
        if (!slide.querySelector('.sticky-gwp-card')) slide.remove();
    });

    const isLoggedIn = !!queryFirst('.js-account-active');
    queryAll('.js-sticky-gwp-join', drawer).forEach(btn => {
        if (isLoggedIn) {
            btn.classList.add('loyalty-enrollnow-cta');
            setAttribute(btn, 'aria-haspopup', 'dialog');
            return;
        }

        if (!btn.getAttribute('href')) {
            btn.classList.add('js-login-link');
            setAttribute(btn, 'aria-haspopup', 'dialog');
            return;
        }

        try {
            const url = new URL(btn.href, window.location.origin);
            url.searchParams.set(GWP_OPEN_JOIN_PARAM, 'true');
            btn.href = url.toString();
        } catch (e) {
            // ignore
        }
    });

    $panel.on('show.bs.modal', () => syncToggleState(drawer, true));
    $panel.on('shown.bs.modal', () => initPanelCarousel(panel));
    $panel.on('hide.bs.modal', () => syncToggleState(drawer, false));

    drawer.addEventListener('click', event => {
        if (event.target.closest(TOGGLE_SELECTOR)) {
            $panel.modal('toggle');
            return;
        }

        const sheetLink = event.target.closest('.js-sticky-gwp-promo-sheet-link');
        if (!sheetLink) return;

        event.preventDefault();
        const { promoSheetId, promoPdSheetAvailable } = sheetLink.dataset;
        openGwpPromoSheet(promoSheetId, promoPdSheetAvailable);
    });

    document.addEventListener('click', event => {
        const target = event.target;
        if (hasClass(panel, SHOW_CLASS) && target.classList && target.classList.contains('modal-backdrop')) {
            $panel.modal('hide');
        }
    });
}

function trackStickyBar() {
    const bar = queryFirst('.cart-stickybar') || queryFirst('.pdp-buybar');
    if (!bar) return;

    const setHeight = () => {
        const height = bar.offsetHeight;
        if (!height) return;
        document.documentElement.style.setProperty('--sticky-bar-height', `${height}px`);
    };

    setHeight();
    new ResizeObserver(setHeight).observe(bar);
}

module.exports = {
    init: function () {
        queryAll('[data-sticky-gwp-drawer]').forEach(initDrawer);
        trackStickyBar();
    }
};
